import { ELECTRICITY_RATE_FLAT_MAX_CENTS } from './pricingCeilings.js';
/**
 * The driver price EVI sets at New job review (Warren, ruling D3, 21 October 2026): "EVI sets the driver price at job
 * review. 'Approve job' requires a price, so launch never waits on one. The review shows a suggested price: the site's
 * utility cost per kWh (ops enters it at review) times a markup I set in Admin settings. Warn when the margin falls
 * below a floor I also set there. If either setting is empty, show no suggestion and ops types the price."
 *
 * One home for the math so the review screen and the Api agree on the suggestion and the warning.
 */
/** The driver price, whole cents per kWh. The cap is a typo guard ($2.00 per kWh, the home guest price cap). */
export const DRIVER_PRICE_MIN_CENTS = 1;
export const DRIVER_PRICE_MAX_CENTS = 200;
/** The site's utility cost goes onto Location.electricity_rate_cents, so it takes that field's flat ceiling: 60, with a
 * second confirmation above 40 (Warren, 21 October 2026, night: "raise the utility and reimbursement caps to 60 cents,
 * with a second confirmation above 40"; pricingCeilings.ts ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS). */
export const UTILITY_COST_MAX_CENTS = ELECTRICITY_RATE_FLAT_MAX_CENTS;
/** Warren's markup: price = utility cost x markup. Below 1 would price under cost. */
export const DRIVER_PRICE_MARKUP_MIN = 1;
export const DRIVER_PRICE_MARKUP_MAX = 10;
/** Warren's margin floor, a percent of the driver price. */
export const DRIVER_PRICE_FLOOR_MIN_PCT = 0;
export const DRIVER_PRICE_FLOOR_MAX_PCT = 95;
/**
 * The minimum margin on the driver price when Settings has none (Warren, 21 October 2026, late evening): "Replace the
 * minimum margin per kWh with one percentage, the minimum margin on the driver price, set to 33%."
 */
export const DRIVER_PRICE_MIN_MARGIN_DEFAULT_PCT = 33;
/** Where the customer asks EVI to review the price (the onboarding link). */
export const DRIVER_PRICE_SUPPORT_EMAIL = 'support@evinitiative.com';
/**
 * The charger types a New job is priced for (Warren, 21 October 2026, evening): "the local public charging price for that
 * charger type (Level 2 or DC)". Level 2 for AC chargers, DC for DC chargers; a job with both gets one price per type.
 */
export const DRIVER_PRICE_CHARGER_TYPES = ['level2', 'dc'];
export const DRIVER_PRICE_CHARGER_TYPE_LABELS = { level2: 'Level 2', dc: 'DC' };
/**
 * How a type's price was set: market (the local public price), floor (the electricity cost at the minimum margin: above
 * the market price, or the area has no market price yet) or manual (EVI ops typed it).
 */
export const DRIVER_PRICE_BASES = ['market', 'floor', 'manual'];
const positive = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;
/** Cents per kWh to whole cents, rounded after six places so float noise never tips a half cent. */
export const wholeDriverCents = (cents) => Math.round(Number(cents.toFixed(6)));
/**
 * The floor in whole cents (Warren, 21 October 2026, late evening): "Floor = the site's electricity cost per kWh divided
 * by (1 minus the margin), so 30.6 cents becomes 45.7 cents. It has no currency." Rounded up to the next whole cent after
 * six places (30.6 at 33% is 45.67, so 46). Null without the cost, or with a margin outside 0 to under 100%.
 */
export function driverPriceFloorCents(utilityCostCents, marginPct) {
    if (!positive(utilityCostCents) || typeof marginPct !== 'number' || !Number.isFinite(marginPct) || marginPct < 0 || marginPct >= 100)
        return null;
    return Math.ceil(Number((utilityCostCents / (1 - marginPct / 100)).toFixed(6)));
}
/** "Price used = the suggested price, or the floor when the suggested price is below it" (Warren, 21 October 2026). */
export function marketDriverPrice(marketCents, floorCents) {
    const market = wholeDriverCents(marketCents);
    return floorCents > market ? { price_cents: floorCents, basis: 'floor' } : { price_cents: market, basis: 'market' };
}
/** How a price was set, in Warren's words: "market price for Los Angeles, Level 2", "floor: electricity plus minimum
 * margin" (the market price was below it). An area with no market price yet reads "floor: no market price for this area
 * yet" (21 October 2026, late evening); a price EVI ops typed reads "set by EVI". */
export function driverPriceBasisLine(p) {
    if (p.basis === 'market')
        return `market price for ${p.market_area || 'the area'}, ${DRIVER_PRICE_CHARGER_TYPE_LABELS[p.type]}`;
    if (p.basis === 'floor')
        return p.market_cents_per_kwh == null ? 'floor: no market price for this area yet' : 'floor: electricity plus minimum margin';
    return 'set by EVI';
}
/** Suggested price in whole cents: round(utility cost x markup). Null when the cost or the markup is missing. */
export function suggestedDriverPriceCents(utilityCostCents, markup) {
    if (!positive(utilityCostCents) || !positive(markup))
        return null;
    // Round the product to 6 places first so a float like 14.7 x 2.5 = 36.75000000000001 rounds as 36.75 would.
    return Math.round(Number((utilityCostCents * markup).toFixed(6)));
}
/** Margin as a percent of the price: (price - cost) / price, to two decimals. Null without both numbers. */
export function driverPriceMarginPct(priceCents, utilityCostCents) {
    if (!positive(priceCents) || !positive(utilityCostCents))
        return null;
    return Math.round(((priceCents - utilityCostCents) * 10000) / priceCents) / 100;
}
/** The warning line when the price's margin is under Warren's floor; null when it is not, or a number is missing. */
export function driverPriceFloorWarning(priceCents, utilityCostCents, marginFloorPct) {
    const margin = driverPriceMarginPct(priceCents, utilityCostCents);
    if (margin === null || typeof marginFloorPct !== 'number' || !Number.isFinite(marginFloorPct))
        return null;
    return margin < marginFloorPct ? `The margin is ${margin}%, below the ${marginFloorPct}% floor.` : null;
}
//# sourceMappingURL=driverPrice.js.map